import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography } from '@/shared/theme/tokens';

/**
 * Custom pagination actions: first («) / prev (‹) / next (›) / last (») buttons.
 * Replaces MUI's default prev/next-only chevrons (AUT-178).
 */
function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const c = useThemeColors();
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const btnSx = { padding: '2px', color: c.textMuted, '&.Mui-disabled': { color: c.borderSubtle } };
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 4 }}>
      <IconButton sx={btnSx} onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        <FirstPageIcon fontSize="small" />
      </IconButton>
      <IconButton sx={btnSx} onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton sx={btnSx} onClick={(e) => onPageChange(e, page + 1)} disabled={page >= lastPage} aria-label="next page">
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      <IconButton sx={btnSx} onClick={(e) => onPageChange(e, lastPage)} disabled={page >= lastPage} aria-label="last page">
        <LastPageIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

/** DataGrid pagination slot wired to the first/last-aware actions above. */
function AppPagination(props) {
  const c = useThemeColors();
  const menuProps = {
    PaperProps: {
      sx: {
        background: c.bgMuted,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        '& .MuiMenuItem-root': {
          fontSize: '11px',
          fontFamily: typography.fontFamily.body,
          color: c.textPrimary,
          '&:hover': { background: c.bgHover },
          '&.Mui-selected': { background: c.bgHover, color: c.textActive },
          '&.Mui-selected:hover': { background: c.bgHover },
        },
      },
    },
  };
  return <GridPagination ActionsComponent={TablePaginationActions} SelectProps={{ MenuProps: menuProps }} {...props} />;
}

/**
 * AppTable — DataGridPro wrapper with project-standard sx baked in.
 * Centralises the ~100-line sx block that every DataGridPro in the app duplicates.
 *
 * Props (all DataGridPro props pass through via ...rest):
 *   rows          — row data
 *   columns       — column defs
 *   getRowId      — custom row id fn (optional)
 *   rowHeight     — default 52
 *   headerHeight  — default 40
 *   extraSx       — merge additional sx overrides without losing the base styles
 */
export function AppTable({
  rows,
  columns,
  getRowId,
  rowHeight = 52,
  headerHeight = 40,
  extraSx,
  serverPagination,
  ...rest
}) {
  const c = useThemeColors();
  const isDark = useIsDark();

  const baseSx = {
    border: 'none',
    fontFamily: typography.fontFamily.body,
    fontSize: '12px',
    height: '100%',
    colorScheme: isDark ? 'dark' : 'light',

    // v7: header area is .MuiDataGrid-container--top; columnHeaders is a scroll container inside it.
    '& .MuiDataGrid-container--top, & .MuiDataGrid-columnHeaders': {
      background: `${c.bgSubtle} !important`,
    },
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler--pinnedRight': {
      background: `${c.bgSubtle} !important`,
    },
    '& .MuiDataGrid-container--top': {
      borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.10)' : c.border} !important`,
    },
    '& .MuiDataGrid-columnHeaders': {
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: c.textMuted,
      textTransform: 'uppercase',
      minHeight: `${headerHeight}px !important`,
    },
    '& .MuiDataGrid-columnHeader': { minHeight: `${headerHeight}px !important` },
    '& .MuiDataGrid-columnSeparator': { opacity: 0, transition: 'opacity 0.15s' },
    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': { opacity: 1, color: c.borderSubtle },
    '& .MuiDataGrid-sortIcon': { color: c.textActive },

    '& .MuiDataGrid-row': {
      transition: 'background 0.12s',
      minHeight: `${rowHeight}px !important`,
      maxHeight: 'none !important',
      '&:hover': { background: c.bgHover },
    },
    '& .MuiDataGrid-cell': {
      borderColor: c.divider,
      color: c.textPrimary,
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px',
      overflow: 'visible',
      whiteSpace: 'normal',
    },

    '& .MuiDataGrid-footerContainer': {
      borderTop: `2px solid ${isDark ? 'rgba(255,255,255,0.10)' : c.border}`,
      background: c.bgSubtle,
      minHeight: '52px',
      overflow: 'hidden',   // stray vertical scroll when the toolbar is ~1px taller
    },
    // The pagination toolbar's default min-height (52px) + footer border can
    // overflow by a pixel — pin it so the footer never scrolls.
    '& .MuiDataGrid-footerContainer .MuiToolbar-root': {
      minHeight: '50px',
    },
    '& .MuiDataGrid-virtualScroller': {
      background: c.bg,
    },
    // Pinned column cells — MUI defaults to white, must match row background
    '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
      background: `${c.bg} !important`,
    },
    '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight': {
      background: `${c.bgHover} !important`,
    },
    '& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': {
      background: `${c.bg} !important`,
      boxShadow: isDark ? '4px 0 8px rgba(0,0,0,0.3)' : '4px 0 8px rgba(30,67,150,0.06)',
    },
    '& .MuiDataGrid-pinnedColumnHeaders': {
      background: `${c.bgSubtle} !important`,
    },
    '& .MuiTablePagination-root': {
      color: c.textMuted,
      fontSize: '11px',
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '11px',
        fontFamily: typography.fontFamily.body,
      },
      // Tighten gap between "Rows per page:" and the selector (AUT-178.1)
      '& .MuiTablePagination-selectLabel': { marginRight: 0 },
      // Border lives on the InputBase wrapper — single border, no double ring
      '& .MuiTablePagination-input': {
        marginLeft: '4px', marginRight: 2,
        border: `1px solid ${c.border}`,
        borderRadius: '5px',
      },
      // Box + downsize the rows-per-page selector (AUT-178.2)
      '& .MuiTablePagination-select': {
        fontSize: '10px',
        fontFamily: typography.fontFamily.body,
        border: 'none',
        padding: '2px 22px 2px 6px',
        '&:focus': { background: 'transparent' },
      },
      '& .MuiTablePagination-selectIcon': { color: c.textMuted },
    },

    '& .MuiSvgIcon-root': { color: c.textMuted },

    // Filter panel operator/value selects — same single-border treatment as pagination
    '& .MuiDataGrid-filterForm': {
      background: c.bgMuted,
      '& .MuiInputBase-root': {
        fontSize: '12px',
        fontFamily: typography.fontFamily.body,
        color: c.textPrimary,
        border: `1px solid ${c.border}`,
        borderRadius: '6px',
        padding: '2px 4px',
        '& fieldset': { border: 'none' },
        '& .MuiSelect-select': { border: 'none', '&:focus': { background: 'transparent' } },
        '& .MuiSvgIcon-root': { color: c.textMuted },
      },
      '& .MuiFormLabel-root': {
        fontSize: '10px',
        fontFamily: typography.fontFamily.body,
        color: c.textSubtle,
      },
      '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
    },

    ...extraSx,
  };

  // Server-pagination mode: BE owns paging. Provide
  //   serverPagination = { rowCount, paginationModel, onPaginationModelChange, pageSizeOptions? }
  // and the grid shows only the current page (no client slicing) with the real
  // total row count. When omitted, the grid client-paginates as before.
  const server = serverPagination
    ? {
        paginationMode: 'server',
        rowCount: serverPagination.rowCount ?? 0,
        paginationModel: serverPagination.paginationModel,
        onPaginationModelChange: serverPagination.onPaginationModelChange,
        pageSizeOptions: serverPagination.pageSizeOptions ?? [10, 25, 50, 100],
        keepNonExistentRowsSelected: false,
      }
    : {
        pageSizeOptions: [10, 25, 50],
        initialState: { pagination: { paginationModel: { pageSize: 10 } } },
      };

  return (
    <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        rowHeight={rowHeight}
        columnHeaderHeight={headerHeight}
        disableRowSelectionOnClick
        pagination
        slots={{ pagination: AppPagination }}
        sx={baseSx}
        {...server}
        {...rest}
      />
  );
}
